import {
  makeObservable,
  observable,
  action,
  computed,
  runInAction,
} from "mobx";
import ProjectInventoryOpsAPI from "../../api/ProjectInventoryOpsAPI";
import {
  InventoryOpsActivityEntityType,
  InventoryOpsActivityLog,
} from "../../../../models/ProjectManagement/InventoryOpsActivityLog";

const PAGE_LIMIT = 30;

/** Local store for the Inventory Ops activity drawer (one PO of one product). */
export class InventoryOpsActivityLogStore {
  private api = new ProjectInventoryOpsAPI();
  /** Guards against an older response overwriting a newer one. */
  private requestSeq = 0;

  orderId: number | null = null;
  productId: number | null = null;
  poId: number | null = null;
  entityType: InventoryOpsActivityEntityType | null = null;
  pageNo: number = 1;
  limit: number = PAGE_LIMIT;

  logs: InventoryOpsActivityLog[] = [];
  totalCount: number = 0;
  isLoading: boolean = false;
  error: string | null = null;

  constructor() {
    makeObservable(this, {
      orderId: observable,
      productId: observable,
      poId: observable,
      entityType: observable,
      pageNo: observable,
      logs: observable,
      totalCount: observable,
      isLoading: observable,
      error: observable,
      totalPages: computed,
      setScope: action,
      setEntityType: action,
      setPage: action,
      fetchLogs: action,
      refreshData: action,
    });
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalCount / this.limit));
  }

  /** Opens the drawer on a PO: resets filter + page, then loads. */
  setScope = (orderId: number, productId: number, poId: number | null) => {
    this.orderId = orderId;
    this.productId = productId;
    this.poId = poId;
    this.entityType = null;
    this.pageNo = 1;
    this.logs = [];
    this.totalCount = 0;
    return this.fetchLogs();
  };

  setEntityType = (entityType: InventoryOpsActivityEntityType | null) => {
    if (this.entityType === entityType) return Promise.resolve();
    this.entityType = entityType;
    this.pageNo = 1;
    return this.fetchLogs();
  };

  setPage = (pageNo: number) => {
    if (pageNo === this.pageNo) return Promise.resolve();
    this.pageNo = pageNo;
    return this.fetchLogs();
  };

  fetchLogs = async () => {
    if (this.orderId == null) return;
    const seq = ++this.requestSeq;
    this.isLoading = true;
    this.error = null;
    try {
      const response = await this.api.getActivityLogs({
        order_id: this.orderId,
        product_id: this.productId ?? undefined,
        po_id: this.poId ?? undefined,
        entity_type: this.entityType ?? undefined,
        page_no: this.pageNo,
        limit: this.limit,
      });
      if (seq !== this.requestSeq) return;
      runInAction(() => {
        this.logs = response?.logs || [];
        this.totalCount = response?.total_count ?? 0;
      });
    } catch (err: any) {
      if (seq !== this.requestSeq) return;
      runInAction(() => {
        this.error =
          err?.response?.data?.message ||
          "Failed to load activity, please try again later";
      });
    } finally {
      if (seq === this.requestSeq) {
        runInAction(() => {
          this.isLoading = false;
        });
      }
    }
  };

  /** Reloads the current page with the applied filter (page kept). */
  refreshData = () => this.fetchLogs();
}
